import { useMemo } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';

import {
  usePageConfig,
  useTabContentConfig,
} from '../../CustomDash/components/PageConfigProvider/PageConfigProvider';
import { ChartRuntimeShell } from '../chart-runtime-shell';
import { GlobalAIAssistantChartFilterScope } from '../global-ai-assistant-chart-filter-scope';
import { HeadlessDigestLeaf } from './headless-digest-leaf';
import type { HeadlessDigestRequest } from './headless-digest-service';
import {
  resolveHeadlessDigest,
  useHeadlessDigestStore,
} from './headless-digest-service';

/**
 * Reads the SYNTHETIC page's config (built by ChartRuntimeShell per request)
 * and mounts the isolated AI filter scope + digest leaf — the same wiring the
 * visible chart portal uses (global-ai-assistant-chart-portal.tsx), minus
 * every visual concern. Must be a child of ChartRuntimeShell.
 */
function HeadlessDigestScope({ request }: { request: HeadlessDigestRequest }) {
  const { config: pageConfig } = usePageConfig();
  const { primaryFilter } = useTabContentConfig();
  return (
    <GlobalAIAssistantChartFilterScope
      messageId={request.requestId}
      filters={request.filters}
      revelioPageId={pageConfig.revelioPageId}
      primaryFilterId={primaryFilter?.id}
      compactLoading
      onApplyResult={(result) => {
        if (!result.ok) {
          // Filter apply failed — the provider would fetch UNFILTERED data,
          // which must never masquerade as the asked figure. Resolve an error
          // digest; unmounting cancels the tree before its query grounds.
          resolveHeadlessDigest(request.requestId, {
            kind: 'chart_data',
            metric: request.item.plot?.defaultMetricOptions?.[0] ?? 'chart',
            chartType: String(request.item.plot?.chartType ?? 'unknown'),
            rowCount: 0,
            empty: true,
            error: true,
            // Carry the REASON, not just the fact — see `errorReason`.
            errorReason: result.error,
          });
        }
        // ok: the leaf's provider stream takes over from here.
      }}
    >
      <HeadlessDigestLeaf request={request} />
    </GlobalAIAssistantChartFilterScope>
  );
}

/**
 * Offscreen mount point for headless digest requests
 * (`fetchHeadlessChartDigest`). One ChartRuntimeShell per in-flight request —
 * the request unmounts when it resolves (store removal). No drawer slot, no
 * IntersectionObserver, no chart card, no settle polling: the provider's
 * render-prop status stream resolves the promise directly.
 */
export function GlobalAiHeadlessDigestHost() {
  const requests = useHeadlessDigestStore((s) => s.requests);
  const { loggedInUser } = useGetLoggedInUser();
  const owner = useMemo(
    () => ({
      id: loggedInUser?.id ?? 'global-ai-headless',
      username: loggedInUser?.username ?? 'global-ai-headless',
    }),
    [loggedInUser?.id, loggedInUser?.username]
  );
  if (requests.length === 0) {
    return null;
  }
  return (
    <div
      style={{ display: 'none' }}
      aria-hidden
      data-testid="global-ai-headless-digest-host"
    >
      {requests.map((request) => (
        <ChartRuntimeShell
          key={request.requestId}
          runtimeKey={request.requestId}
          owner={owner}
          entityType={request.entityType}
          chartItem={request.item}
          postingsMode={request.postingsMode}
        >
          <HeadlessDigestScope request={request} />
        </ChartRuntimeShell>
      ))}
    </div>
  );
}
